import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { SubscriptionService } from '@/services/subscriptionService';
import { SubscriptionPlans } from './SubscriptionPlans';
import { CurrentSubscription } from './CurrentSubscription';
import { Loader2, AlertTriangle } from 'lucide-react';
import { differenceInDays } from 'date-fns';

interface SubscriptionCenterProps {
  organizationId: string;
}

export function SubscriptionCenter({ organizationId }: SubscriptionCenterProps) {
  const [searchParams] = useSearchParams();
  const [showPlans, setShowPlans] = useState(searchParams.get('view') === 'pricing');
  const [isLoadingBilling, setIsLoadingBilling] = useState(false);

  useEffect(() => {
    if (searchParams.get('view') === 'pricing') {
      setShowPlans(true);
    }
  }, [searchParams]);

  const { data: subscription, isLoading, error } = useQuery({
    queryKey: ['subscription', organizationId],
    queryFn: () => SubscriptionService.getCurrentSubscription(organizationId),
    enabled: !!organizationId,
  });

  const { data: trialStatus } = useQuery({
    queryKey: ['trialStatus', organizationId],
    queryFn: () => SubscriptionService.getOrganizationTrialStatus(organizationId),
    enabled: !!organizationId,
  });

  const handleViewPlans = () => {
    setShowPlans(true);
  };

  const handleBackToSummary = () => {
    setShowPlans(false);
  };

  const handleManageBilling = async () => {
    try {
      setIsLoadingBilling(true);
      const result = await SubscriptionService.createPortalSession(
        organizationId,
        window.location.origin + '/admin/subscription-center'
      );
      
      if (result?.url) {
        window.location.href = result.url;
      } else {
        console.error('No URL returned from portal session');
        setIsLoadingBilling(false);
      }
    } catch (error) {
      console.error('Failed to create portal session:', error);
      setIsLoadingBilling(false);
    }
  };

  const getTrialDaysRemaining = () => {
    if (!trialStatus) return 0;
    return trialStatus.trialDaysRemaining || 0;
  };

  if (isLoading) {
    return (
      <Card className="w-full">
        <CardContent className="pt-6 pb-6 flex flex-col items-center justify-center space-y-3">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading subscription details...</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>
          Failed to load subscription information. Please try again later.
        </AlertDescription>
      </Alert>
    );
  }

  // If we're showing the plans page
  if (showPlans) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle>Subscription Plans</CardTitle>
          <CardDescription>Choose a plan that works for your organization</CardDescription>
        </CardHeader>
        <CardContent>
          <SubscriptionPlans organizationId={organizationId} onSubscribed={handleBackToSummary} currentPlanId={subscription?.subscription_price_id} />
        </CardContent>
        <CardFooter>
          <Button variant="outline" onClick={handleBackToSummary}>
            Back to Summary
          </Button>
        </CardFooter>
      </Card>
    );
  }

  // If trial (either from subscription or trial status)
  const isTrialUser = trialStatus?.isInTrial || subscription?.status === 'trialing';
  if (isTrialUser) {
    const daysRemaining = getTrialDaysRemaining();
    
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle>Subscription</CardTitle>
          <CardDescription>Manage your subscription</CardDescription>
        </CardHeader>
        <CardContent>
          <Alert className="mb-4">
            <AlertTitle>Free Trial</AlertTitle>
            <AlertDescription>
              You are currently on a free trial. {daysRemaining > 0 
                ? `Your trial expires in ${daysRemaining} day${daysRemaining === 1 ? '' : 's'}.` 
                : 'Your trial has expired.'}
            </AlertDescription>
          </Alert>
          {subscription && <CurrentSubscription subscription={subscription} onManageBilling={handleManageBilling} />}
        </CardContent>
        <CardFooter className="flex flex-col sm:flex-row gap-3 justify-between">
          <Button onClick={handleViewPlans} variant="default" className="w-full sm:w-auto">
            {daysRemaining > 0 ? 'Upgrade Now' : 'Choose a Plan'}
          </Button>
          {subscription?.status === 'active' && (
            <Button 
              onClick={handleManageBilling} 
              variant="outline"
              disabled={isLoadingBilling}
              className="w-full sm:w-auto"
            >
              {isLoadingBilling ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Loading...
                </>
              ) : (
                'Manage Billing'
              )}
            </Button>
          )}
        </CardFooter>
      </Card>
    );
  }

  // If active subscription or other status
  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>Subscription</CardTitle>
        <CardDescription>Manage your subscription</CardDescription>
      </CardHeader>
      <CardContent>
        {subscription 
          ? <CurrentSubscription subscription={subscription} onManageBilling={handleManageBilling} /> 
          : <Alert>
              <AlertTitle>No Subscription</AlertTitle>
              <AlertDescription>
                You don't have an active subscription. Choose a plan to get started.
              </AlertDescription>
            </Alert>
        }
      </CardContent>
      <CardFooter className="flex flex-col sm:flex-row gap-3 justify-between">
        <Button onClick={handleViewPlans} variant="default" className="w-full sm:w-auto">
          {subscription?.status === 'active' ? 'View Plans' : 'Choose a Plan'}
        </Button>
        {subscription?.status === 'active' && (
          <Button 
            onClick={handleManageBilling} 
            variant="outline"
            disabled={isLoadingBilling}
            className="w-full sm:w-auto"
          >
            {isLoadingBilling ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Loading...
              </>
            ) : (
              'Manage Billing'
            )}
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}